/* eslint-disable react-refresh/only-export-components -- the hook ships beside its provider; HMR falls back to reload here. */
import * as React from 'react';

/**
 * Which register the controls under this point are drawn in.
 *
 * `touch` is the POS default: 44px fields and buttons sized for a cashier
 * at arm's length. Buttons share the same flat styling as `desk`.
 *
 * `desk` is the seated console — office, settings, platform (Solid UI lock,
 * owner 2026-10-09, blueprint Frame C). Every field, select and button that
 * asks for a POS step — unsized or `default` (44px) — renders at the 36px `sm`
 * step instead, icon buttons at their 36px square. 36,
 * not the blueprint's 32: the owner reviewed 32px fields in the app and asked
 * for "input size little bit more"; fields and buttons move together so a
 * field and the button beside it stay one height (Kumo's own default). An
 * explicit `compact` or `xs` is a toolbar's deliberate choice and keeps its
 * size, as do `lg` and `xl`. The provider also stamps
 * `data-register="desk"` on <html> for the few rules only CSS can apply —
 * sentence-case `micro` labels (tokens.css) — so a portalled dialog matches.
 *
 * React context carries the same control sizing through portalled Dialogs,
 * Sheets and DropdownMenus, including those rendered outside the shell DOM.
 *
 * An explicit `size` always wins. A coarse pointer still lifts `sm` to 44px
 * through the density floor in tokens.css, so a tablet keeps thumb targets.
 */
export type ControlRegister = 'touch' | 'desk';

const ControlRegisterContext = React.createContext<ControlRegister>('touch');

export function ControlRegisterProvider({
  value,
  children,
}: {
  value: ControlRegister;
  children: React.ReactNode;
}) {
  React.useEffect(() => {
    if (value !== 'desk') return;
    const root = document.documentElement;
    root.dataset.register = 'desk';
    return () => {
      delete root.dataset.register;
    };
  }, [value]);
  return (
    <ControlRegisterContext.Provider value={value}>{children}</ControlRegisterContext.Provider>
  );
}

/** The desk register's field step for a requested size (Input, SelectTrigger). */
export function deskFieldSize<S extends string>(size: S | undefined, register: ControlRegister) {
  if (register !== 'desk') return size ?? ('default' as S);
  return (size === undefined || size === 'default' ? 'sm' : size) as S;
}

export function useControlRegister(): ControlRegister {
  return React.useContext(ControlRegisterContext);
}
